import { Blocks } from "blockly/core";
import { javascriptGenerator } from "blockly/javascript";
import * as Blockly from "blockly";
import { TSprite } from "../../../../models/objects/sprite";
import { TObstacle, TScene } from "../../../../models/objects/objects";
import { getImagePath } from "../../../../utils/format/valueFormat";

export interface ProjectBlocksCatalog {
  scenes: TScene[];
  sprites: TSprite[];
  obstacles: TObstacle[];
  spriteThumbnails: Record<string, string>;
}

export const PROJECT_BLOCK_NONE = "NONE";
type ImageOption = { src: string; width: number; height: number; alt: string };
type DropdownOption = [ImageOption | string, string];
const KIND_FN: Record<string, string> = {
  IMMOVABLE: "addImmovable",
  DECOR: "addDecor",
  PICKABLE: "addPickable",
  PUSHABLE: "addPushable",
};

const toImageOptions = (
  items: { name: string; src: string }[],
  width: number,
  height: number,
): DropdownOption[] => {
  if (!items.length) return [["Aucun élément", PROJECT_BLOCK_NONE]];
  return items.map(({ name, src }) =>
    src ? [{ src, width, height, alt: name }, name] : [name, name],
  );
};

export function defineProjectBlocks(catalog: ProjectBlocksCatalog): void {
  const sceneOptions = () =>
    toImageOptions(
      catalog.scenes.map((scene) => ({
        name: scene.name,
        src: scene.img ? getImagePath(scene.img) : "",
      })),
      64,
      40,
    );
  const spriteOptions = () =>
    toImageOptions(
      catalog.sprites.map((sprite) => ({
        name: sprite.name,
        src:
          catalog.spriteThumbnails[sprite.name] ||
          (sprite.image_url ? getImagePath(sprite.image_url) : ""),
      })),
      40,
      40,
    );
  const obstacleOptions = () =>
    toImageOptions(
      catalog.obstacles.map((obstacle) => ({
        name: obstacle.name,
        src: obstacle.img ? getImagePath(obstacle.img) : "",
      })),
      40,
      40,
    );

  Blocks["project_set_scene"] = {
    init() {
      this.appendDummyInput()
        .appendField("choisir la scène")
        .appendField(new Blockly.FieldDropdown(sceneOptions), "SCENE");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(20);
      this.setTooltip("Choisit l'image de fond de la scène.");
    },
  };
  javascriptGenerator.forBlock["project_set_scene"] = (block: any) => {
    const name = block.getFieldValue("SCENE");
    return name && name !== PROJECT_BLOCK_NONE
      ? `\n  highlightBlock('${block.id}');\n  setScene(${JSON.stringify(name)});\n`
      : `highlightBlock('${block.id}');\n`;
  };

  Blocks["project_add_character"] = {
    init() {
      this.appendDummyInput()
        .appendField("ajouter le personnage")
        .appendField(new Blockly.FieldDropdown(spriteOptions), "SPRITE")
        .appendField("x")
        .appendField(new Blockly.FieldNumber(100, 0, 1000, 1), "X")
        .appendField("y")
        .appendField(new Blockly.FieldNumber(100, 0, 1000, 1), "Y");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(20);
      this.setTooltip(
        "Ajoute un personnage de la bibliothèque à la position (x, y).",
      );
    },
  };
  javascriptGenerator.forBlock["project_add_character"] = (block: any) => {
    const name = block.getFieldValue("SPRITE");
    if (!name || name === PROJECT_BLOCK_NONE)
      return `highlightBlock('${block.id}');\n`;
    const x = Number(block.getFieldValue("X")) || 0;
    const y = Number(block.getFieldValue("Y")) || 0;
    return `\n  highlightBlock('${block.id}');\n  addCharacter(${JSON.stringify(name)}, ${x}, ${y});\n`;
  };

  const kindOptions: [string, string][] = [
    ["un obstacle", "IMMOVABLE"],
    ["un décor", "DECOR"],
    ["un objet à ramasser", "PICKABLE"],
    ["un objet à pousser", "PUSHABLE"],
  ];
  Blocks["project_add_obstacle"] = {
    init() {
      this.appendDummyInput()
        .appendField("ajouter")
        .appendField(new Blockly.FieldDropdown(kindOptions), "KIND")
        .appendField(new Blockly.FieldDropdown(obstacleOptions), "OBSTACLE")
        .appendField("x")
        .appendField(new Blockly.FieldNumber(200, 0, 1000, 1), "X")
        .appendField("y")
        .appendField(new Blockly.FieldNumber(200, 0, 1000, 1), "Y");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(20);
      this.setTooltip(
        "Ajoute un objet de la bibliothèque à la position (x, y).",
      );
    },
  };
  javascriptGenerator.forBlock["project_add_obstacle"] = (block: any) => {
    const name = block.getFieldValue("OBSTACLE");
    if (!name || name === PROJECT_BLOCK_NONE)
      return `highlightBlock('${block.id}');\n`;
    const x = Number(block.getFieldValue("X")) || 0;
    const y = Number(block.getFieldValue("Y")) || 0;
    return `\n  highlightBlock('${block.id}');\n  ${KIND_FN[block.getFieldValue("KIND")] || "addImmovable"}(${JSON.stringify(name)}, ${x}, ${y});\n`;
  };

  Blocks["project_set_win"] = {
    init() {
      this.appendDummyInput().appendField("gagner la partie");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(20);
      this.setTooltip("Déclare la partie gagnée.");
    },
  };
  javascriptGenerator.forBlock["project_set_win"] = (block: any) =>
    `\n  highlightBlock('${block.id}');\n  setWin();\n`;
}
